import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  normalizeError,
  getErrorCodeMessage,
  getFieldForErrorCode,
  isErrorCodeInGroup,
  type BusinessErrorCode,
} from "@envirohub/utils";

/**
 * Resolved, UI-ready description of how an auth error should be handled.
 * Pages read these fields to show inline alerts, map field errors, redirect,
 * or disable actions - without inspecting HTTP status or raw error shapes.
 */
export interface AuthErrorAction {
  /** Backend business code, when present. */
  code: BusinessErrorCode | null;
  /** Localized, user-facing message (always populated). */
  message: string;
  /** Field name -> first message, ready for react-hook-form `setError`. */
  fieldErrors: Record<string, string>;
  /** Path to redirect to (e.g. `/verify` for EMAIL_NOT_VERIFIED), if any. */
  redirectTo: string | null;
  /** Account-state errors that should block access (suspended/locked/...). */
  isBlocking: boolean;
  /** Whether the user is being throttled. */
  isRateLimited: boolean;
}

/** Flatten the backend `fieldErrors` map (field -> string[]) to first message. */
function flattenFieldErrors(
  fieldErrors: Record<string, string[]> | undefined,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!fieldErrors) return result;
  Object.entries(fieldErrors).forEach(([field, messages]) => {
    const [firstMessage] = messages ?? [];
    if (firstMessage) result[field] = firstMessage;
  });
  return result;
}

/**
 * Hook that turns any thrown error from an auth mutation into an
 * {@link AuthErrorAction}. Keeps the auth pages thin and consistent.
 */
export function useAuthError() {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown): AuthErrorAction => {
      const apiError = normalizeError(error);
      const code = apiError.errorCode ?? null;
      const message = getErrorCodeMessage(error, t);

      const fieldErrors = flattenFieldErrors(apiError.fieldErrors);

      // Surface conflict/duplicate codes on their mapped field too.
      const mappedField = getFieldForErrorCode(code);
      if (mappedField && !fieldErrors[mappedField]) {
        fieldErrors[mappedField] = message;
      }

      const redirectTo = code === "EMAIL_NOT_VERIFIED" ? "/verify" : null;

      return {
        code,
        message,
        fieldErrors,
        redirectTo,
        isBlocking: isErrorCodeInGroup(code, "ACCOUNT_STATE"),
        isRateLimited: isErrorCodeInGroup(code, "RATE_LIMIT"),
      };
    },
    [t],
  );
}
